/** @jsx jsx */
import { jsx } from '@emotion/react';

import { compose } from 'recompose';

import ErrorBoundaryWithTagging from 'bundles/common/components/ErrorBoundaryWithTagging';
import type {
  Props as PropsForDataProvider,
  SkillSetSearchResults,
} from 'bundles/enterprise-learner-search/components/skillsetSearch/SkillSetSearchDataProvider';
import { withSkillSetSearchResults } from 'bundles/enterprise-learner-search/components/skillsetSearch/SkillSetSearchDataProvider';
import { IdTypes } from 'bundles/product-features';

import { withSkillSearchResults } from '../skillSearch/SkillSearchDataProvider';
import type { SkillSearchResults } from '../skillSearch/SkillSearchDataProvider';
import SkillSearchResultBanner from '../skillSearch/SkillSearchResultBanner';
import NewSkillSetSearchResultBanner from './NewSkillSetSearchResultBanner';
import SkillSetSearchResultBanner from './SkillSetSearchResultBanner';
import { withProductFeatures } from './featureFlags';

type PropsFromParent = {
  programHomeLink: string;
  programSlug: string;
  showDivider: boolean;
  thirdPartyOrgId: string;
};

type PropsFromDataProviders = {
  skillHits: SkillSearchResults[];
  skillsetHits: SkillSetSearchResults[];
  enableSkillsDashboard: boolean;
};

type Props = PropsFromDataProviders & PropsFromParent & PropsForDataProvider;

const SearchResultBannerWithData = ({
  enableSkillsDashboard,
  programHomeLink,
  skillHits,
  skillsetHits,
  programSlug,
  showDivider,
  currentRefinement,
}: Props) => {
  if (skillsetHits.length === 0 && skillHits.length === 0) {
    return null;
  }

  // We  don't want to show the skills to organizations that don't have the skills dashboard
  if (!enableSkillsDashboard) {
    return (
      <SkillSetSearchResultBanner
        showDivider={showDivider}
        programHomeLink={programHomeLink}
        skillsetHits={skillsetHits}
      />
    );
  }

  // Show Skills when there is at least one result
  const showSkills = skillHits.length > 0;

  if (showSkills) {
    return (
      <SkillSearchResultBanner
        programSlug={programSlug}
        programHomeLink={programHomeLink}
        skillHits={skillHits}
        currentRefinement={currentRefinement}
      />
    );
  } else if (skillsetHits.length > 0) {
    return <NewSkillSetSearchResultBanner programHomeLink={programHomeLink} skillsetHits={skillsetHits} />;
  }

  return null;
};

const SearchResultBanner = compose<Props, PropsFromParent & PropsForDataProvider>(
  withProductFeatures<{ enableSkillsDashboard: boolean }, PropsFromParent>(
    ({ thirdPartyOrgId }) => ({ idType: IdTypes.Organization, id: thirdPartyOrgId }),
    ({ features }) => ({
      enableSkillsDashboard: features.get('enterprise', 'enableSkillsDashboard'),
    })
  ),
  withSkillSearchResults,
  withSkillSetSearchResults()
)(SearchResultBannerWithData);

const SearchResultBannerWithErrorBoundary = (props: PropsFromParent & PropsForDataProvider) => (
  <ErrorBoundaryWithTagging componentName="search_result_banner">
    <SearchResultBanner {...props} />
  </ErrorBoundaryWithTagging>
);

export { SearchResultBannerWithData };
export default SearchResultBannerWithErrorBoundary;
